import {
  mdiFileImage,
  mdiFileOutline,
  mdiFilePdfBox,
  mdiMicrosoftExcel,
  mdiMicrosoftWord,
  mdiMovieOpenPlay,
  mdiVolumeHigh
} from '@mdi/js';
import type { UploadFile } from 'antd';
import { message, Upload } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import type { Attachment, StorageDownloadTemplates } from 'common/models';
import type { ImageViewerItem } from 'components/ImageViewer/ImageViewer';
import type JSZip from 'jszip';
import type { SupportTicketAttachment } from 'pages/support/models';

import { getDownloadUriTemplates } from './tokenManager';
import { getI18nTranslate } from './translationHelper';

export const MAX_ATTACHMENT_FILENAME_LENGTH = 150;

export enum AttachmentThumbnail {
  IMAGE = 'image',
  SOUND = 'sound',
  PDF = 'file-pdf',
  VIDEO = 'video-camera',
  EXCEL = 'file-excel',
  WORD = 'file-word',
  FILE = 'file'
}

const extensionReg = /(?:\.([^.]+))?$/;

const getExtension = (fileName: string) => {
  const result = extensionReg.exec(fileName);
  return result?.[1]?.toLowerCase();
};

export const isImage = (
  attachment: Attachment | SupportTicketAttachment | ImageViewerItem
): boolean => {
  const extension = getExtension(attachment.publicUrl);
  return extension === 'png' || extension === 'jpg' || extension === 'jpeg';
};

export const handleAttachmentThumbnail = (
  attachment: Attachment | SupportTicketAttachment | ImageViewerItem
): string => {
  const extension = getExtension(attachment.publicUrl);
  if (extension === 'png' || extension === 'jpg' || extension === 'jpeg') {
    return mdiFileImage;
  }

  if (extension === 'mp3' || extension === 'wav') {
    return mdiVolumeHigh;
  }

  if (extension === 'pdf') {
    return mdiFilePdfBox;
  }

  if (extension === 'mp4' || extension === 'mkv' || extension === 'wmv' || extension === 'mpeg') {
    return mdiMovieOpenPlay;
  }

  if (extension === 'xls' || extension === 'xlsx') {
    return mdiMicrosoftExcel;
  }

  if (extension === 'doc' || extension === 'docx') {
    return mdiMicrosoftWord;
  }

  return mdiFileOutline;
};

export const getAttachmentBlobUrl = (attachment: Attachment) => {
  const downloadUriTemplates = getDownloadUriTemplates();

  if (downloadUriTemplates) {
    return downloadUriTemplates.attachment?.replace(
      '{blob_filename}',
      encodeURIComponent(attachment.internalStorageId)
    );
  }
};

export const getSupportTicketAttachmentBlobUrl = (attachment: SupportTicketAttachment) => {
  const downloadUriTemplates = getDownloadUriTemplates();

  if (downloadUriTemplates) {
    return downloadUriTemplates.supportTicketAttachment?.replace(
      '{blob_filename}',
      encodeURIComponent(attachment.internalStorageId)
    );
  }
};

export const getSuperadminSupportTicketAttachmentBlobUrl = (
  attachment: SupportTicketAttachment,
  templates?: StorageDownloadTemplates
) => {
  if (templates) {
    return templates.supportTicketAttachment?.replace(
      '{blob_filename}',
      encodeURIComponent(attachment.internalStorageId)
    );
  }
};

// Reads the file name from a Content-Disposition response header (RFC 5987 UTF-8 form first,
// then the plain `filename=` fallback). Returns undefined when no name is present.
export const parseContentDispositionFileName = (
  contentDisposition?: string
): string | undefined => {
  if (!contentDisposition) return undefined;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through to plain filename
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plainMatch?.[1];
};

export const getExcelBlobUrl = (
  internalStorageId: string,
  storageType: string | null | undefined,
  templates?: StorageDownloadTemplates
) => {
  if (!templates) return undefined;
  const template =
    storageType === 'ExcelExport' ? templates.excelExport : templates.excelImport;
  return template?.replace('{blob_filename}', encodeURIComponent(internalStorageId));
};

export const getUploadListAttachmentIds = (fileList: UploadFile[]) => {
  return fileList.reduce((acc: string[], file) => {
    if (file.status === 'done') {
      return [...acc, file.response.id];
    }

    return acc;
  }, []);
};

export const handleFilenameLength = async (file: RcFile) => {
  if (file.name.length > MAX_ATTACHMENT_FILENAME_LENGTH) {
    await message.error(
      getI18nTranslate('errors.maxFilenameLength', {
        limit: MAX_ATTACHMENT_FILENAME_LENGTH
      })
    );
    return Upload.LIST_IGNORE;
  } else {
    return true;
  }
};

export const transformAndValidateFilename = (filename: string) => {
  let rg1 = /[\\/:\*\?"<>\|\s]/g; // forbidden characters \ / : * ? " < > |
  let rg2 = /^\./; // cannot start with dot (.)
  let rg3 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i; // forbidden file names

  // Replace forbidden characters with an underscore
  const replacedFilename = filename.replace(rg1, '_');

  if (!rg2.test(replacedFilename) && !rg3.test(replacedFilename)) {
    return replacedFilename;
  }

  return 'nepodporovany_nazev';
};

export const getFilenameWithExtension = (attachment: Attachment) => {
  const splittedFilename = attachment.name.replace(extensionReg, ''); // Remove the extension from the filename
  const extensionFromUrl = getExtension(attachment.publicUrl);
  const filenameExtension = getExtension(attachment.name);

  if (extensionFromUrl !== filenameExtension) {
    return `${transformAndValidateFilename(attachment.name)}.${extensionFromUrl}`;
  } else {
    return `${transformAndValidateFilename(splittedFilename)}.${extensionFromUrl}`;
  }
};

export const transformSizeToMB = (sizeInBytes?: number): string => {
  if (typeof sizeInBytes !== 'number') return '-';

  const mb = sizeInBytes / (1024 * 1024);
  // Format integer part with spaces
  const [intPart, decimalPart] = mb.toFixed(2).split('.');
  const formattedInt = Number(intPart).toLocaleString().replace(/,/g, ' ');
  return `${formattedInt}.${decimalPart} MB`;
};

export const getUniqueZipFilename = (zip: JSZip, filename: string): string => {
  if (!zip.file(filename)) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;
  const baseName = hasExtension ? filename.slice(0, lastDotIndex) : filename;
  const extension = hasExtension ? filename.slice(lastDotIndex) : '';

  let counter = 2;
  let candidate = `${baseName} (${counter})${extension}`;

  while (zip.file(candidate)) {
    counter += 1;
    candidate = `${baseName} (${counter})${extension}`;
  }

  return candidate;
};
